"use client";
import { useState, useEffect } from "react";
import { SessionControls } from "@/components/session-controls";
import { ConnectButton } from "./connect-button";
import { ConnectionState } from "livekit-client";
import { motion, AnimatePresence } from "framer-motion";
import {
  useConnectionState,
  useVoiceAssistant,
  BarVisualizer,
} from "@livekit/components-react";
import { ChatControls } from "@/components/chat-controls";
import { useAgent } from "@/hooks/use-agent";
import { useConnection } from "@/hooks/use-connection";
import { toast } from "@/hooks/use-toast";
import Image from "next/image";
import qsIcon from "../assets/images/qs-icon.png";
import {
  decryptValue,
  getQueryParam,
  getCurrentUTCDateTime,
  getUrlBasedOnEnv,
} from "@/app/utility/utilities";
import { LogsData } from "@/data/logs-data";

interface ChatProps {
  setIsConfigVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setIsConfigVisibleLeft: React.Dispatch<React.SetStateAction<boolean>>;
  isConfigVisible: boolean;
  isConfigVisibleLeft: boolean;
  instructions: string;
  session_id: string;
  isNewLayout: boolean;
  voiceID: string;
}

export function Chat({
  setIsConfigVisible,
  isConfigVisible,
  setIsConfigVisibleLeft,
  isConfigVisibleLeft,
  instructions,
  voiceID,
  session_id,
  isNewLayout,
}: ChatProps) {
  const connectionState = useConnectionState();
  const { audioTrack, state } = useVoiceAssistant();
  const [isChatRunning, setIsChatRunning] = useState(false);
  const { agent } = useAgent();
  const { disconnect } = useConnection();
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);
  const [hasSeenAgent, setHasSeenAgent] = useState(false);
  const [egressId, setEgressId] = useState<string | null>(null);
  const [localRoomName, setLocalRoomName] = useState<string | null>(null);
  const [hasEgressStarted, setHasEgressStarted] = useState(false); // Track egress start
  const [hasEgressStopped, setHasEgressStopped] = useState(false); // Track egress stop
  const { token, roomName: connectionRoomName } = useConnection();
  const [saveToken, setSaveToken] = useState("");
  const [isSessionDone, setIsSessionDone] = useState(false);
  const { displayTranscriptions } = useAgent();
  const [wakeLock, setWakeLock] = useState<WakeLockSentinel | null>(null);
  useEffect(() => {
    let disconnectTimer: NodeJS.Timeout | undefined;
    let appearanceTimer: NodeJS.Timeout | undefined;
    if (connectionState === ConnectionState.Connected && !agent) {
      appearanceTimer = setTimeout(() => {
        disconnect();
        setHasSeenAgent(false);
        toast({
          title: "Agent Unavailable",
          description:
            "Unable to connect to an agent right now. Please try again later.",
          variant: "destructive",
        });
      }, 5000);
    }
    if (agent) {
      setHasSeenAgent(true);
    }
    if (
      connectionState === ConnectionState.Connected &&
      !agent &&
      hasSeenAgent
    ) {
      disconnectTimer = setTimeout(() => {
        if (!agent) {
          disconnect();
          setHasSeenAgent(false);
        }
        toast({
          title: "Agent Disconnected",
          description:
            "The AI agent has unexpectedly left the conversation. Please try again.",
          variant: "destructive",
        });
      }, 5000);
    }

    if (!isChatRunning && connectionState === ConnectionState.Connected && hasSeenAgent) {
      setIsChatRunning(
        connectionState === ConnectionState.Connected && hasSeenAgent,
      );
    } 
    
    if (isChatRunning && connectionState === ConnectionState.Disconnected) {
      setIsChatRunning(false);
    }
    
    if(isChatRunning && (connectionState === ConnectionState.Reconnecting || connectionState === ConnectionState.SignalReconnecting)) {
      const logsData: LogsData = {
        log_type: ConnectionState.Reconnecting ? 'Reconnecting' : 'Disconnected',
        log_message: ConnectionState.Reconnecting ? 'Connection State Reconnecting' : 'Connection State Disconnected'
      }

      sendLiveKitLogs(logsData);
    }

    if(isChatRunning && connectionState === ConnectionState.Connecting) {
      const logsData: LogsData = {
        log_type: 'Connecting',
        log_message: 'Connecting'
      }

      sendLiveKitLogs(logsData);
    }

    if(isChatRunning && (connectionState === ConnectionState.Disconnected)) {
      const logsData: LogsData = {
        log_type: 'Disconnected',
        log_message: 'Connection State Disconnected'
      }

      sendLiveKitLogs(logsData);
    }

    if(connectionState === ConnectionState.Connected && hasSeenAgent) {
      const logsData: LogsData = {
        log_type: 'Connected',
        log_message: 'Connection State Connected'
      }

      sendLiveKitLogs(logsData);
    }
    return () => {
      if (disconnectTimer) clearTimeout(disconnectTimer);
      if (appearanceTimer) clearTimeout(appearanceTimer);
    };
  }, [connectionState, agent, disconnect, hasSeenAgent]);
  
  useEffect(() => {
    const handleStart = async () => {
      try {
        let egressParams: boolean | null = null;
        await startGenerateVod("start", connectionRoomName, token).then(() => {
          egressParams = true;
        });
      } catch (error) {
        console.error("Error during VOD or egress start process:", error);
      }
    };

    const handleStop = async () => {
      try {
        await startGenerateVod("end", connectionRoomName, ""); // Call startGenerateVod after stopping egress
      } catch (error) {
        console.error(
          "Error during egress stop or VOD generation process:",
          error,
        );
      }
    };

    // Handle chat start
    if (isChatRunning && connectionRoomName && !hasEgressStarted) {
      handleStart();
    }

    // Handle chat stop
    if (!isChatRunning && egressId && !hasEgressStopped) {
      handleStop();
    }

    // Reset connection states when chat ends
    if (!isChatRunning) {
      setLocalRoomName(null);
      setHasEgressStarted(false);
      setHasEgressStopped(false);
      setEgressId(null);
    }
  }, [isChatRunning, connectionRoomName]);

  useEffect(() => {
    checkSessionStatus();
  },[]);

  useEffect(() => {
    // Function to request wake lock
    const requestWakeLock = async () => {
      if ("wakeLock" in navigator) {
        try {
          const lock = await navigator.wakeLock.request("screen");
          setWakeLock(lock);
        } catch (err) {
          console.error("Error requesting wake lock:", err);
        }
      } else {
        console.warn("Wake Lock API is not supported in this browser.");
      }
    };

    // Acquire wake lock when chat is running
    if (isChatRunning) {
      requestWakeLock();
    }

    // Cleanup function to release wake lock
    return () => {
      if (wakeLock) {
        wakeLock
          .release()
          .then(() => console.log("Wake lock released"))
          .catch((err) => console.error("Failed to release wake lock:", err));
        setWakeLock(null);
      }
    };
  }, [isChatRunning]);
  
  const toggleInstructionsEdit = () =>
    setIsEditingInstructions(!isEditingInstructions);
  const renderVisualizer = () => (
    <div className="flex w-full items-center">
      <div className="h-[160px] mt-16 md:mt-0 lg:pb-24 w-full">
        <BarVisualizer
          state={state}
          barCount={5}
          trackRef={audioTrack}
          className="w-full h-full"
        />
      </div>
    </div>
  );

  const renderConnectionControl = () => {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={isChatRunning ? "session-controls" : "connect-button"}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ type: "tween", duration: 0.15, ease: "easeInOut" }}
        >
          {isChatRunning ? (
            <SessionControls
              isSessionDone={isSessionDone}
              setIsSessionDone={setIsSessionDone}
            />
          ) : (
            <ConnectButton
              instructions={instructions}
              voiceID={voiceID}
              isSessionDone={isSessionDone}
            />
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  // Function to call the API to Check the Session Status
  const checkSessionStatus = async () => {
    const formData = new FormData();
    formData.append("study_id", decryptValue(getQueryParam("study_id")));
    formData.append("session_id", decryptValue(getQueryParam("session_id")));
    formData.append("sr_id", getQueryParam("sr_id"));
    formData.append(
      "study_user_id",
      decryptValue(getQueryParam("study_user_id")),
    );
    formData.append(
      "participant_code",
      decryptValue(getQueryParam("part_code")),
    );
    formData.append("tour_id", decryptValue(getQueryParam("tour_id")));
    fetch(
      `${getUrlBasedOnEnv(decryptValue(getQueryParam("env")))}/voice-ai/generate-vod`,
      {
        method: "POST",
        body: formData,
      },
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`API error: ${response.statusText}`);
        }
        return response.json();
      })
      .then(async (data) => {
        if (data.success === 0) {
          setIsSessionDone(true);
          toast({
            title: data.msg,
            variant: "destructive",
          });
        } else {
          setIsSessionDone(false);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch session data:", error);
      });
  };

  let globalTime = 0.0; // Initialize a global time tracker

  const formattedTranscript = {
    name: "OpenAiTranscript_to_QualsightsTranscript",
    metadata: {},
    done: true,
    response: {
      type: "",
      results: [
        {
          alternatives: [
            {
              words: displayTranscriptions.flatMap(
                ({ segment, participant }) => {
                  const wordsArray = segment.text.trim().split(/\s+/);
                  const totalWords = wordsArray.length;
                  const startTimeSeconds = segment.firstReceivedTime / 1000; // Convert to seconds
                  const endTimeSeconds = segment.lastReceivedTime / 1000; // Convert to seconds
                  const durationPerWord =
                    (endTimeSeconds - startTimeSeconds) / totalWords || 0.5; // Avoid division by zero

                  return wordsArray.map((word: string, index: number) => {
                    const wordStartTime = globalTime; // Use the global time tracker
                    const wordEndTime = globalTime + durationPerWord;
                    globalTime = wordEndTime; // Update global time for the next word

                    return {
                      startTime: wordStartTime.toFixed(2),
                      endTime: wordEndTime.toFixed(2),
                      word: word,
                      speakerTag: 0,
                      speakerLabel: participant?.isAgent
                        ? "AI Agent"
                        : decryptValue(getQueryParam("p_name")),
                    };
                  });
                },
              ),
              transcript: displayTranscriptions
                .map(({ segment }) => segment.text.trim())
                .join(" "),
            },
          ],
        },
      ],
    },
  };
  // Function to call the API to Generate VOD
  const startGenerateVod = async (param: any, roomName: any, token: any) => {
    const formData = new FormData();
    formData.append("study_id", decryptValue(getQueryParam("study_id")));
    formData.append("session_id", decryptValue(getQueryParam("session_id")));
    formData.append("sr_id", getQueryParam("sr_id"));
    formData.append(
      "study_user_id",
      decryptValue(getQueryParam("study_user_id")),
    );
    formData.append("tour_id", decryptValue(getQueryParam("tour_id")));
    formData.append(
      "participant_code",
      decryptValue(getQueryParam("part_code")),
    );
    if (param === "start") {
      formData.append("start_date", getCurrentUTCDateTime());
      formData.append("room_name", roomName);
      formData.append("token", token);
    } else {
      formData.append("end_date", getCurrentUTCDateTime());
      formData.append("room_name", roomName);
      formData.append("egress_id", egressId || "");
      formData.append("token", saveToken);
      formData.append(
        "voicesight_transcript",
        JSON.stringify(formattedTranscript),
      );
    }

    fetch(
      `${getUrlBasedOnEnv(decryptValue(getQueryParam("env")))}/voice-ai/generate-vod`,
      {
        method: "POST",
        body: formData,
      },
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`API error: ${response.statusText}`);
        }
        return response.json();
      })
      .then(async (data) => {
        if (data.success === 0) {
          await disconnect();
          toast({
            title: data.msg,
            variant: "destructive",
          });
        } else {
          setEgressId(data.egress_id);
          if (param === "end" && data.api === "end") {
            setSaveToken("");
            window.location.href = process.env.NEXT_PUBLIC_LIVEKIT_SESSION_DONE as string;
          } else {
            setSaveToken(token);
          }
        }
      })
      .catch((error) => {
        console.error("Failed to fetch session data:", error);
      });
  };

  const sendLiveKitLogs = async (logData: LogsData) => {
    const formData = new FormData();
    formData.append("study_id", decryptValue(getQueryParam("study_id")));
    formData.append("session_id", decryptValue(getQueryParam("session_id")));
    formData.append(
      "study_user_id",
      decryptValue(getQueryParam("study_user_id")),
    );
    formData.append(
      "participant_code",
      decryptValue(getQueryParam("part_code")),
    );
    formData.append("tour_id", decryptValue(getQueryParam("tour_id")));
    formData.append("room_name", connectionRoomName || '');
    formData.append("log_type", logData.log_type);
    formData.append("log_message", logData.log_message);
    fetch(
      `${getUrlBasedOnEnv(decryptValue(getQueryParam("env")))}/voice-ai/save-socket-logs`,
      {
        method: "POST",
        body: formData,
      },
    ).then((response) => {
      if (!response.ok) {
        throw new Error(`Logs API error: ${response.statusText}`);
      }
      return response.json();
    }).then(async (data) => {
      if (data.success === 0) {
        toast({
          title: data.msg,
          variant: "destructive",
        });
      } else {
        console.log("api logs response", data);
      }
    }).catch((error) => {
      console.error("Failed to send logs data:", error);
    });
  }

  return (
    <div className="flex flex-col h-full overflow-hidden p-2 lg:p-4 bg-white">
      <ChatControls
        showEditButton={isChatRunning}
        isEditingInstructions={isEditingInstructions}
        onToggleEdit={toggleInstructionsEdit}
      />
      <div className="flex flex-col flex-grow items-center lg:justify-between lg:mt-0">
        <div className="flex flex-col flex-grow w-full">
          <div className="flex items-center justify-center w-full">
            {/* Mobile View */}
            <div className="lg:hidden w-full">
              {isChatRunning && !isEditingInstructions ? (
                renderVisualizer()
              ) : (
                <div
                  className={`rounded-full border-6 border-white border-opacity-20 w-52 h-52 mx-auto mb-0 ${
                    isNewLayout ? "" : "mt-[50%] sm:mt-[0%]"
                  }`}
                >
                  <div className="rounded-full w-48 h-48 mx-auto mt-1 text-center flex items-center justify-center">
                    <Image
                      src={qsIcon}
                      alt="qsLogo"
                      style={{ width: "300px" }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Desktop View */}
            <div className="hidden lg:block">
              <div className="ar rounded-full border-6 border-white border-opacity-20 w-52 h-52 mx-auto mb-0 lg:mt-[50%] mt-0">
                <div className="rounded-full w-48 h-48 mx-auto mt-1 text-center flex items-center justify-center">
                  <Image src={qsIcon} alt="qsLogo" style={{ width: "400px" }} />
                </div>
              </div>
            </div>
          </div>

          <div className="grow h-full flex items-center justify-center">
            <div className="w-full hidden lg:block">
              {isChatRunning && !isEditingInstructions && renderVisualizer()}
            </div>
          </div>
        </div>
        <div className="md:mt-2 md:pt-2 md:mb-12 max-md:fixed max-md:bottom-12 max-md:left-1/2 max-md:-translate-x-1/2 max-md:z-50 xl:fixed xl:bottom-12 xl:left-1/2 xl:-translate-x-1/2 xl:z-50">
          {renderConnectionControl()}
        </div>
      </div>
    </div>
  );
}
